import React, { createContext, useContext, useState, useMemo } from 'react';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);

    const addToCart = (product) => {
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === product.id);
            if (existingItem) {
                return prevCart.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prevCart, { ...product, quantity: 1 }];
        });
    };

    const removeFromCart = (productId) => {
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === productId);
            if (!existingItem) return prevCart;

            if (existingItem.quantity === 1) {
                return prevCart.filter(item => item.id !== productId);
            }
            return prevCart.map(item =>
                item.id === productId
                    ? { ...item, quantity: item.quantity - 1 }
                    : item
            );
        });
    };

    const deleteItem = (productId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== productId));
    };

    const setQuantity = (product, quantity) => {
        const qty = parseInt(quantity, 10);
        setCart(prevCart => {
            if (qty <= 0 || isNaN(qty)) {
                return prevCart.filter(item => item.id !== product.id);
            }
            const existingItem = prevCart.find(item => item.id === product.id);
            if (existingItem) {
                return prevCart.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: qty }
                        : item
                );
            }
            return [...prevCart, { ...product, quantity: qty }];
        });
    };

    const clearCart = () => setCart([]);

    const replaceCart = (items) => setCart(items || []);

    const getCartQuantity = (productId) => {
        const item = cart.find(item => item.id === productId);
        return item ? item.quantity : 0;
    };

    const cartTotals = useMemo(() => {
        return cart.reduce((totals, item) => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            const vatRate = parseFloat(item.vatRate) || 0;
            const includesVat = !!item.priceIncludesVat;

            let exVatPrice = price;
            let vatAmountPerUnit = 0;

            if (includesVat) {
                exVatPrice = price / (1 + vatRate / 100);
                vatAmountPerUnit = price - exVatPrice;
            } else {
                vatAmountPerUnit = price * (vatRate / 100);
            }

            const itemSubtotalExVat = exVatPrice * quantity;
            const itemVat = vatAmountPerUnit * quantity;
            const itemGrandTotal = (exVatPrice + vatAmountPerUnit) * quantity;

            // Calculate savings based on difference between originalPrice and active price
            const originalPrice = parseFloat(item.originalPrice) || price;
            const savingsPerUnit = Math.max(0, originalPrice - price);
            const itemSavings = savingsPerUnit * quantity;

            return {
                subTotalExVat: totals.subTotalExVat + itemSubtotalExVat,
                totalVat: totals.totalVat + itemVat,
                grandTotal: totals.grandTotal + itemGrandTotal,
                items: totals.items + quantity,
                totalSavings: totals.totalSavings + itemSavings
            };
        }, { subTotalExVat: 0, totalVat: 0, grandTotal: 0, items: 0, totalSavings: 0 });
    }, [cart]);

    return (
        <CartContext.Provider value={{
            cart,
            addToCart,
            removeFromCart,
            deleteItem,
            setQuantity,
            clearCart,
            replaceCart,
            getCartQuantity,
            subTotalExVat: cartTotals.subTotalExVat.toFixed(2),
            totalVat: cartTotals.totalVat.toFixed(2),
            grandTotal: cartTotals.grandTotal.toFixed(2),
            totalAmount: cartTotals.grandTotal.toFixed(2),
            totalItems: cartTotals.items,
            totalSavings: cartTotals.totalSavings.toFixed(2)
        }}>
            {children}
        </CartContext.Provider>
    );
};
